import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { toast } from "sonner"
import { useTranslation } from "@/hooks/use-translation"
import { GameLayout, GameStatusAlert } from '@/components/games/common'
import { GameShortcuts } from '@/components/games/common/game-shortcuts'
import { Palette } from 'lucide-react'
import { saveGameResult, getUserStats, getGameResult, hasPlayedToday } from '@/lib/game-stats'
import { InContentAd } from '@/components/adsense'
import { getDateString } from '@/lib/dictionaries'

interface GameState {
  sequence: string[]
  userSequence: string[]
  gameStatus: 'ready' | 'showing' | 'playing' | 'won' | 'lost'
  currentLevel: number
  showingIndex: number
  maxLevel: number
  isFlashing: boolean
  flashingColor: string | null
  failedAttemptsByLevel: { [level: number]: number } // Rastrear intentos fallidos por nivel
  attemptsByLevel: { [level: number]: number } // Intentos totales por nivel
  currentAttempt: number // Intento actual en el nivel (1-3)
}

interface ColorButton {
  id: string
  color: string
  bgColor: string
  activeColor: string
  name: string
}

const colors: ColorButton[] = [
  { id: 'red', color: 'red', bgColor: 'bg-red-500', activeColor: 'bg-red-300', name: 'Rojo' },
  { id: 'blue', color: 'blue', bgColor: 'bg-blue-500', activeColor: 'bg-blue-300', name: 'Azul' },
  { id: 'green', color: 'green', bgColor: 'bg-green-500', activeColor: 'bg-green-300', name: 'Verde' },
  { id: 'yellow', color: 'yellow', bgColor: 'bg-yellow-500', activeColor: 'bg-yellow-300', name: 'Amarillo' }
]

// Función para generar una secuencia diaria completa
function generateDailySequence(language: string, maxLevel: number): string[] {
  const today = new Date()
  const dateString = getDateString(today)

  // Crear seed base
  let seed = 0
  const seedString = dateString + language
  for (let i = 0; i < seedString.length; i++) {
    seed = ((seed << 5) - seed + seedString.charCodeAt(i)) & 0xffffffff
  }

  // Función PRNG simple y efectiva
  const rng = (function (seed: number) {
    return function () {
      seed = (seed * 9301 + 49297) % 233280
      return seed / 233280
    }
  })(Math.abs(seed))

  const sequence: string[] = []
  const colorIds = colors.map(c => c.id)

  for (let i = 0; i < maxLevel; i++) {
    let nextColorIndex: number

    // Para los primeros colores, permitir cualquiera
    if (i < 2) {
      nextColorIndex = Math.floor(rng() * colorIds.length)
    } else {
      // Evitar que se repita el mismo color más de 2 veces seguidas
      const lastColor = sequence[i - 1]
      const secondLastColor = sequence[i - 2]

      if (lastColor === secondLastColor) {
        // Forzar un color diferente
        const availableColors = colorIds.filter(id => id !== lastColor)
        nextColorIndex = colorIds.indexOf(availableColors[Math.floor(rng() * availableColors.length)])
      } else {
        nextColorIndex = Math.floor(rng() * colorIds.length)
      }
    }

    sequence.push(colorIds[nextColorIndex])
  }

  return sequence
}

// Función simplificada para obtener color de una secuencia pregenerada
function getDailySequence(language: string, level: number): string {
  // Generar la secuencia completa una vez y tomar el color del nivel específico
  const fullSequence = generateDailySequence(language, 10) // 10 es el maxLevel
  return fullSequence[level - 1] || colors[0].id // level-1 porque los niveles empiezan en 1
}

export function MemoriaColores() {
  const { t, language } = useTranslation()
  const [gameState, setGameState] = useState<GameState>({
    sequence: [],
    userSequence: [],
    gameStatus: 'ready',
    currentLevel: 1,
    showingIndex: 0,
    maxLevel: 10,
    isFlashing: false,
    flashingColor: null,
    failedAttemptsByLevel: {},
    attemptsByLevel: {},
    currentAttempt: 1
  })

  // Nuevo estado para controlar si estamos en modo "nuevo juego"
  const [, setIsNewGameMode] = useState(false)

  // Estado para controlar el modal de primera vez en móvil
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false)

  // Flag para evitar guardado automático durante la carga inicial
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)

  const today = getDateString()
  const [, setStats] = useState(getUserStats(`memoria-colores-${language}`))

  // Función para guardar el estado parcial del juego
  const savePartialGameState = (state: GameState) => {
    if (state.gameStatus === 'playing' || state.gameStatus === 'ready') {
      const partialState = {
        sequence: state.sequence,
        userSequence: state.userSequence,
        gameStatus: state.gameStatus,
        currentLevel: state.currentLevel,
        showingIndex: state.showingIndex,
        failedAttemptsByLevel: state.failedAttemptsByLevel,
        attemptsByLevel: state.attemptsByLevel,
        currentAttempt: state.currentAttempt,
        date: today
      }
      localStorage.setItem(`game-memoria-colores-${language}-partial`, JSON.stringify(partialState))
    }
  }

  // Función para cargar el estado parcial del juego
  const loadPartialGameState = (): Partial<GameState> | null => {
    const key = `game-memoria-colores-${language}-partial`
    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        // Verificar que la fecha guardada coincida con el día actual
        if (parsed.date === today) {
          return parsed
        } else {
          // Si la fecha no coincide, limpiar el estado parcial
          localStorage.removeItem(key)
          return null
        }
      } catch {
        return null
      }
    } else {
      return null
    }
  }

  // Función para limpiar el estado parcial (cuando se completa el juego)
  const clearPartialGameState = () => {
    localStorage.removeItem(`game-memoria-colores-${language}-partial`)
  }

  useEffect(() => {
    // Reiniciar flag de carga inicial
    setIsInitialLoadComplete(false)

    // Cargar estadísticas del localStorage específicas por idioma
    setStats(getUserStats(`memoria-colores-${language}`))

    // Verificar si ya se jugó hoy
    if (hasPlayedToday(`memoria-colores-${language}`, today)) {
      const todayResult = getGameResult(`memoria-colores-${language}`, today)
      if (todayResult) {

        if (todayResult.won) {
          // Si ganó, mostrar nivel 10 y generar la secuencia completa
          const fullSequence = generateDailySequence(language, 10)
          setGameState(prev => ({
            ...prev,
            gameStatus: 'won',
            currentLevel: 10, // Mostrar nivel final
            sequence: fullSequence // Secuencia completa de 10 niveles
          }))
        } else {
          // Si perdió, mostrar el nivel donde perdió
          const level = todayResult.attempts.length
          setGameState(prev => ({
            ...prev,
            gameStatus: 'lost',
            currentLevel: level,
            sequence: todayResult.attempts // La secuencia ya está guardada correctamente
          }))
        }

        // Limpiar estado parcial si ya se completó el juego
        clearPartialGameState()
      }
    } else {
      // Intentar cargar estado parcial si no se ha completado el juego
      const partialState = loadPartialGameState()
      if (partialState) {
        setGameState(prev => ({
          ...prev,
          sequence: partialState.sequence || [],
          userSequence: partialState.userSequence || [],
          gameStatus: partialState.gameStatus || 'ready',
          currentLevel: partialState.currentLevel || 1,
          showingIndex: partialState.showingIndex || 0,
          failedAttemptsByLevel: partialState.failedAttemptsByLevel || {},
          attemptsByLevel: partialState.attemptsByLevel || {},
          currentAttempt: partialState.currentAttempt || 1
        }))
      }
    }

    // Verificar si es la primera vez en móvil
    const hasSeenInstructions = localStorage.getItem(`game-memoria-colores-${language}-instructions`)
    if (!hasSeenInstructions && window.innerWidth < 1024) { // lg breakpoint
      setShowFirstTimeModal(true)
    }

    // Marcar que la carga inicial está completa
    setIsInitialLoadComplete(true)
  }, [language, today])

  // Efecto para guardar el estado parcial cuando cambie el gameState
  useEffect(() => {
    if (!isInitialLoadComplete) {
      return
    }

    // Guardar estado parcial en más situaciones
    if (gameState.gameStatus === 'playing' ||
      gameState.gameStatus === 'ready' ||
      gameState.gameStatus === 'showing') {
      savePartialGameState(gameState)
    }
  }, [gameState, isInitialLoadComplete])

  const generateSequence = useCallback((level: number) => {
    const sequence: string[] = []
    for (let i = 1; i <= level; i++) {
      const nextColor = getDailySequence(language, i)
      sequence.push(nextColor)
    }

    return sequence
  }, [language])

  const startGame = () => {
    const sequence = generateSequence(gameState.currentLevel)
    const newState: GameState = {
      ...gameState,
      sequence,
      userSequence: [],
      gameStatus: 'showing' as const,
      showingIndex: 0,
      isFlashing: false,
      flashingColor: null,
      currentAttempt: 1
    }
    setGameState(newState)
    // Guardar inmediatamente el estado de inicio
    savePartialGameState(newState)
  }

  useEffect(() => {
    if (gameState.gameStatus !== 'showing') return
    if (gameState.sequence.length === 0) return

    let timeoutId: NodeJS.Timeout

    const showNext = (index: number) => {
      if (index < gameState.sequence.length) {
        const currentColor = gameState.sequence[index]

        // Flash the color
        setGameState(prev => ({
          ...prev,
          isFlashing: true,
          flashingColor: currentColor,
          showingIndex: index
        }))

        timeoutId = setTimeout(() => {
          setGameState(prev => ({
            ...prev,
            isFlashing: false,
            flashingColor: null
          }))

          timeoutId = setTimeout(() => showNext(index + 1), 300)
        }, 600)
      } else {
        // Sequence complete, start user input
        setGameState(prev => ({
          ...prev,
          gameStatus: 'playing',
          showingIndex: 0
        }))
      }
    }

    timeoutId = setTimeout(() => showNext(0), 1000)

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [gameState.gameStatus, gameState.sequence])

  const handleColorClick = (colorId: string) => {
    if (gameState.gameStatus !== 'playing') return

    const newUserSequence = [...gameState.userSequence, colorId]
    const isCorrect = colorId === gameState.sequence[gameState.userSequence.length]

    if (!isCorrect) {
      // Wrong color - increment failed attempts for current level
      const newFailedAttempts = {
        ...gameState.failedAttemptsByLevel,
        [gameState.currentLevel]: (gameState.failedAttemptsByLevel[gameState.currentLevel] || 0) + 1
      }

      // Check if we have more attempts left
      if (gameState.currentAttempt < 3) {
        // Still have attempts left, try again
        setGameState(prev => ({
          ...prev,
          userSequence: [],
          currentAttempt: prev.currentAttempt + 1,
          failedAttemptsByLevel: newFailedAttempts
        }))

        toast.error(t("games.memoriaColores.wrongColor", { attempt: gameState.currentAttempt + 1 }), {
          duration: 2000
        })
        return
      } else {
        // No more attempts - game over
        setGameState(prev => ({
          ...prev,
          gameStatus: 'lost',
          userSequence: newUserSequence,
          failedAttemptsByLevel: newFailedAttempts
        }))

        // Generar la secuencia hasta el nivel donde perdió
        const sequenceUntilLoss = generateDailySequence(language, gameState.currentLevel)
        // Calcular el total de intentos sumando todos los niveles
        const totalAttempts = Object.values(gameState.attemptsByLevel).reduce((sum, attempts) => sum + (attempts || 0), 0)
        saveGameResult(`memoria-colores-${language}`, sequenceUntilLoss, false, today, {
          level: gameState.currentLevel,
          attemptsByLevel: gameState.attemptsByLevel,
          totalAttempts
        })
        setStats(getUserStats(`memoria-colores-${language}`))
        // Resetear modo nuevo juego al completar una partida
        setIsNewGameMode(false)
        // Limpiar estado parcial al completar el juego
        clearPartialGameState()

        toast.error(t("games.memoriaColores.gameOver", { level: gameState.currentLevel }), {
          duration: 4000
        })
        return
      }
    }

    // Correct color
    setGameState(prev => ({
      ...prev,
      userSequence: newUserSequence
    }))

    // Check if sequence is complete
    if (newUserSequence.length === gameState.sequence.length) {
      // Level complete - update attempts for this level
      // Los intentos reales son currentAttempt - 1 (porque currentAttempt comienza en 1)
      const realAttempts = gameState.currentAttempt - 1
      const newAttemptsByLevel = {
        ...gameState.attemptsByLevel,
        [gameState.currentLevel]: realAttempts
      }

      if (gameState.currentLevel >= gameState.maxLevel) {
        // Game won!
        // Generar la secuencia completa para mostrar
        const fullSequence = generateDailySequence(language, 10)

        setGameState(prev => ({
          ...prev,
          gameStatus: 'won',
          attemptsByLevel: newAttemptsByLevel,
          sequence: fullSequence // Usar la secuencia completa
        }))

        // Calcular el total de intentos sumando todos los niveles
        const totalAttempts = Object.values(newAttemptsByLevel).reduce((sum, attempts) => sum + (attempts || 0), 0)
        saveGameResult(`memoria-colores-${language}`, fullSequence, true, today, {
          level: gameState.maxLevel,
          attemptsByLevel: newAttemptsByLevel,
          totalAttempts
        })
        setStats(getUserStats(`memoria-colores-${language}`))
        // Resetear modo nuevo juego al completar una partida
        setIsNewGameMode(false)
        // Limpiar estado parcial al completar el juego
        clearPartialGameState()

        toast.success(t("games.memoriaColores.congratulations"), {
          duration: 4000
        })
      } else {
        // Next level
        setTimeout(() => {
          const newState: GameState = {
            ...gameState,
            currentLevel: gameState.currentLevel + 1,
            userSequence: [],
            gameStatus: 'ready' as const,
            attemptsByLevel: newAttemptsByLevel,
            currentAttempt: 1
          }
          setGameState(newState)
          // Guardar inmediatamente el nuevo estado
          savePartialGameState(newState)
        }, 1000)
      }
    }
  }



  // Función para volver a ver la secuencia (útil cuando ya se jugó hoy)
  const showSequenceAgain = () => {
    if (gameState.sequence.length > 0) {
      setGameState(prev => ({
        ...prev,
        gameStatus: 'showing',
        userSequence: [],
        isFlashing: false,
        flashingColor: null
      }))
    }
  }



  const handleCloseFirstTimeModal = () => {
    setShowFirstTimeModal(false)
    localStorage.setItem(`game-memoria-colores-${language}-instructions`, 'true')
  }

  const renderGameContent = () => (
    <>
      {/* Game Status - Show above the game board */}
      {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') && (
        <GameStatusAlert status={gameState.gameStatus}>
          <div className="space-y-1">
            <div className="font-bold">
              {gameState.gameStatus === 'won'
                ? t("games.memoriaColores.youWon", { level: gameState.maxLevel })
                : t("games.memoriaColores.youLost", { level: gameState.currentLevel })
              }
            </div>
            <div className="text-xs font-medium">
              {t("games.memoriaColores.comeBackTomorrow")}
            </div>
          </div>
        </GameStatusAlert>
      )}

      {/* Game Board */}
      <div className="space-y-6">
        {/* Game Info */}
        <div className="text-center space-y-2">
          <h2 className="text-lg lg:text-xl font-bold">{t("games.memoriaColores.title")}</h2>
          <p className="text-xs lg:text-sm text-muted-foreground">{t("games.memoriaColores.subtitle")}</p>
          <div className="flex items-center justify-center gap-2 lg:gap-4 text-xs lg:text-sm">
            <Badge variant="outline">
              {t("games.memoriaColores.level")} {gameState.currentLevel}
            </Badge>
            <Badge variant="outline">
              {t("statistics.maxStreak")}: {gameState.maxLevel}
            </Badge>
            {gameState.gameStatus === 'playing' && (
              <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {t("games.memoriaColores.attempt")} {gameState.currentAttempt}/3
              </Badge>
            )}
          </div>
        </div>

        {/* Game Status Messages */}
        {gameState.gameStatus === 'ready' && (
          <div className="text-center space-y-3 lg:space-y-4">
            <p className="text-base lg:text-lg font-medium">{t("games.memoriaColores.clickStart")}</p>
            <Button onClick={startGame} size="lg" className="px-6 lg:px-8">
              {gameState.currentLevel === 1 ? t("games.memoriaColores.startGame") : t("games.memoriaColores.nextLevel")}
            </Button>
          </div>
        )}

        {gameState.gameStatus === 'showing' && (
          <div className="text-center space-y-3 lg:space-y-4">
            <p className="text-base lg:text-lg font-medium">{t("games.memoriaColores.watchSequence")}</p>
            <Button onClick={showSequenceAgain} variant="outline" size="sm">
              {t("games.memoriaColores.showSequence")}
            </Button>
          </div>
        )}

        {gameState.gameStatus === 'playing' && (
          <div className="text-center space-y-3 lg:space-y-4">
            <p className="text-base lg:text-lg font-medium">{t("games.memoriaColores.repeatSequence")}</p>
            <p className="text-xs lg:text-sm text-muted-foreground">{t("games.memoriaColores.yourSequence")}</p>
          </div>
        )}

        {/* Color Grid */}
        <div className={`grid grid-cols-2 ${(gameState.gameStatus === 'playing' || gameState.gameStatus === 'ready' || gameState.gameStatus === 'showing') ? 'gap-3 lg:gap-4' : 'gap-1 lg:gap-2'} ${(gameState.gameStatus === 'playing' || gameState.gameStatus === 'ready' || gameState.gameStatus === 'showing') ? 'max-w-xs' : 'max-w-[160px]'} mx-auto`}>
          {colors.map((color) => {
            const isFlashing = gameState.isFlashing && gameState.flashingColor === color.id
            const isClickable = gameState.gameStatus === 'playing'

            return (
              <button
                key={color.id}
                className={`
                  aspect-square rounded-lg
                  transition-all duration-200 transform relative
                  ${(gameState.gameStatus === 'playing' || gameState.gameStatus === 'ready' || gameState.gameStatus === 'showing') ? '' : 'w-16 h-16 lg:w-20 lg:h-20'}
                  ${color.bgColor}
                  ${isFlashing ?
                    `${color.activeColor} scale-125 border-white shadow-2xl brightness-150 ring-4 ring-white ring-opacity-80` :
                    ''
                  }
                  ${isClickable ? 'hover:scale-105 hover:brightness-110' : ''}
                  ${!isClickable ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                  ${isFlashing ? 'animate-pulse z-10' : ''}
                `}
                style={isFlashing ? {
                  boxShadow: '0 0 30px rgba(255, 255, 255, 0.8), 0 0 60px rgba(255, 255, 255, 0.4)',
                  filter: 'brightness(1.6) saturate(1.3)'
                } : {}}
                onClick={() => handleColorClick(color.id)}
                disabled={!isClickable}
                aria-label={color.name}
              />
            )
          })}
        </div>

        {/* User Sequence Display */}
        {gameState.gameStatus === 'playing' && gameState.userSequence.length > 0 && (
          <div className="text-center">
            <div className="text-xs lg:text-sm text-muted-foreground mb-2">
              {t("games.memoriaColores.yourSequence")}:
            </div>
            <div className="flex justify-center gap-1 lg:gap-2">
              {gameState.userSequence.map((colorId, index) => {
                const color = colors.find(c => c.id === colorId)
                return (
                  <div
                    key={index}
                    className={`w-5 h-5 lg:w-6 lg:h-6 rounded ${color?.bgColor} border border-gray-300`}
                  />
                )
              })}
            </div>
          </div>
        )}

        {/* Mostrar secuencia cuando ya se jugó hoy */}
        {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') && gameState.sequence.length > 0 && (
          <div className="text-center space-y-2 lg:space-y-3">
            <div className="text-xs lg:text-sm text-muted-foreground">
              {t("games.memoriaColores.todaysSequence")}:
            </div>
            <div className="flex justify-center gap-1 lg:gap-2">
              {gameState.sequence.map((colorId, index) => {
                const color = colors.find(c => c.id === colorId)
                return (
                  <div
                    key={index}
                    className={`w-6 h-6 lg:w-8 lg:h-8 rounded-lg ${color?.bgColor} border-2 border-gray-300 shadow-md`}
                    title={color?.name}
                  />
                )
              })}
            </div>
          </div>
        )}
      </div>
    </>
  )



  return (
    <GameLayout>
      {/* Desktop Layout */}
      <div className="hidden lg:flex lg:flex-row lg:gap-8 lg:px-8 lg:py-8">
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className="w-full max-w-xl xl:max-w-2xl">
            {renderGameContent()}
          </div>
        </div>

        {/* Sidebar - Desktop only - Siempre mostrar reglas */}
        <div className="w-80 min-w-80 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Palette className="h-4 w-4" />
                {t("games.memoriaColores.howToPlay")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs">{t("games.memoriaColores.rules.main")}</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-xs">{t("games.memoriaColores.rules.watch")}</Badge>
                  <span className="text-xs">{t("games.memoriaColores.rules.watchDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-secondary text-xs">{t("games.memoriaColores.rules.repeat")}</Badge>
                  <span className="text-xs">{t("games.memoriaColores.rules.repeatDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-muted text-muted-foreground text-xs">{t("games.memoriaColores.rules.advance")}</Badge>
                  <span className="text-xs">{t("games.memoriaColores.rules.advanceDesc")}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advertisement below "Cómo Jugar" on desktop */}
          <InContentAd className="w-full" compact />

          {/* Game Shortcuts - Always show on desktop */}
          <GameShortcuts currentGame="memoria-colores" />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden min-h-[calc(100dvh-64px)] flex flex-col">
        {/* Advertisement above game board on mobile */}
        <InContentAd className="w-full" compact />

        <div className="flex-1 flex flex-col py-2 overflow-y-auto">
          <div className="w-full py-4">
            {renderGameContent()}

            {/* Game Shortcuts - Mobile */}
            {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost' || hasPlayedToday(`memoria-colores-${language}`, today)) && (
              <GameShortcuts currentGame="memoria-colores" />
            )}
          </div>
        </div>
      </div>

      {/* Modal de primera vez - Solo móvil */}
      <Dialog open={showFirstTimeModal} onOpenChange={setShowFirstTimeModal}>
        <DialogContent className="max-w-sm mx-auto mt-20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              {t("games.memoriaColores.howToPlay")}
            </DialogTitle>
            <DialogDescription className="text-left space-y-3">
              <p className="text-sm">{t("games.memoriaColores.rules.main")}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-500 text-xs">{t("games.memoriaColores.rules.watch")}</Badge>
                  <span className="text-xs">{t("games.memoriaColores.rules.watchDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500 text-xs">{t("games.memoriaColores.rules.repeat")}</Badge>
                  <span className="text-xs">{t("games.memoriaColores.rules.repeatDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-purple-500 text-xs">{t("games.memoriaColores.rules.advance")}</Badge>
                  <span className="text-xs">{t("games.memoriaColores.rules.advanceDesc")}</span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button onClick={handleCloseFirstTimeModal}>
              {t("navigation.gotIt")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </GameLayout>
  )
} 